/**
 * Working-day (Mon-Fri) arithmetic, mirroring `zh-backend/src/common/helpers/working-days.util.ts`.
 *
 * Task templates store schedule-relative offsets in working days, and the import rebuilds
 * real dates from them server-side. Anything that previews those dates before the import
 * has to use the same arithmetic, or the preview shows dates the user will not get.
 */

const isWeekend = (date: Date): boolean => date.getDay() === 0 || date.getDay() === 6;

/**
 * Move a date forward to the next working day. A weekday is returned unchanged.
 *
 * The import anchors on this before adding any offset, so offsets 0 and 1 land on
 * distinct days even when the user picks a weekend start date.
 */
export function nextWorkingDay(date: Date): Date {
  const result = new Date(date);
  while (isWeekend(result)) {
    result.setDate(result.getDate() + 1);
  }
  return result;
}

/** Add N working days to a date. A negative or non-finite count adds nothing. */
export function addWorkingDays(date: Date, days: number): Date {
  const result = new Date(date);
  if (!Number.isFinite(days)) return result;

  let remaining = Math.floor(days);
  while (remaining > 0) {
    result.setDate(result.getDate() + 1);
    if (!isWeekend(result)) remaining--;
  }
  return result;
}
